/**
 * Grille tarifaire Signapharma (CHF HT).
 * Calcul des prix pour les 3 familles de croix de pharmacie uniquement.
 * Les autres produits (lettres / châssis / néon intérieur) restent
 * "sur demande" — pas de prix temps réel.
 *
 * Source des prix : tarif communiqué par le client.
 */

// -- Croix classique LED --
// Points de référence (cm → CHF). Interpolation linéaire entre points.
const CLASSIC_SF_POINTS = [
  [30, 600], [40, 600], [50, 700], [60, 800], [70, 900], [80, 1000],
];
const CLASSIC_DF_POINTS = [
  [30, 1100], [40, 1100], [50, 1200], [60, 1400], [70, 1500], [80, 1600], [100, 2000],
];

// -- Croix point-à-point --
// Tailles fixes uniquement — lookup exact.
const DOTMATRIX_SF = { 60: 1000, 80: 1200 };
const DOTMATRIX_DF = { 60: 1600, 80: 1800 };
const DOTMATRIX_INT = { 30: 300, 50: 400, 60: 600 };

// -- Croix néon LED --
// Interpolation linéaire entre 30 cm = 300 CHF et 80 cm = 900 CHF.
// Pour S cm : P = 300 + (S - 30) * 12
function neonPrice(sizeCm) {
  const s = Math.max(30, Math.min(120, sizeCm));
  const price = 300 + (s - 30) * 12;
  return Math.round(price);
}

function interp(points, x) {
  if (!points.length) return 0;
  // Hors bornes : on extrapole en suivant la pente du dernier segment.
  if (x <= points[0][0]) return points[0][1];
  if (x >= points[points.length - 1][0]) return points[points.length - 1][1];
  for (let i = 0; i < points.length - 1; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[i + 1];
    if (x >= x1 && x <= x2) {
      if (x2 === x1) return y1;
      const t = (x - x1) / (x2 - x1);
      return Math.round(y1 + t * (y2 - y1));
    }
  }
  return points[points.length - 1][1];
}

/**
 * Calcule le prix d'UN item de croix.
 * @param {object} item - { designId, sizeCm, ... }
 * @param {string} family - "classic" | "dotmatrix" | "neon"
 * @param {object} design - élément de ALL_CROSS_DESIGNS (avec face: "simple"|"double"|"interieur")
 * @returns {{ priceCHF: number, breakdown: Array<{label:string, value:number}> }}
 */
export function computeItemPrice({ item, family, design }) {
  if (!item || !design) return { priceCHF: 0, breakdown: [] };
  // Item vide (placeholder _empty_) → pas de prix.
  if (item.designId && String(item.designId).startsWith("_empty_")) {
    return { priceCHF: 0, breakdown: [] };
  }
  const sizeCm = Number(item.sizeCm) || 0;
  const breakdown = [];
  let price = 0;

  if (family === "classic") {
    // Pour les croix classiques, l'info "simple/double face" est portée par
    // l'ITEM (pas par le design). On lit donc en priorité item.face si
    // disponible, sinon on retombe sur design.face (croix dot-matrix).
    const face = item.face || design.face || "simple";
    const isDF = face === "double";
    // Nouvelle grille tarifaire unifiée :
    //   30 cm = 800 CHF (SF) / 30 cm = 800 CHF (DF)
    //   80 cm = 1600 CHF (SF & DF)
    //   Interpolation linéaire, pas de 100 cm pour le DF (cap 80).
    const sizeCmCapped = Math.min(80, sizeCm);
    const slope = (1600 - 800) / (80 - 30); // 16 CHF / cm
    price = Math.round(800 + (sizeCmCapped - 30) * slope);
    if (sizeCmCapped < 30) price = 800;
    breakdown.push({
      label: `${isDF ? "Croix double face" : "Croix simple face"} ${sizeCm}×${sizeCm} cm`,
      value: price,
    });
  } else if (family === "dotmatrix") {
    const face = design.face;
    let table = DOTMATRIX_SF;
    let label = `Point-à-point simple face`;
    if (face === "double") { table = DOTMATRIX_DF; label = "Point-à-point double face"; }
    else if (face === "interieur") { table = DOTMATRIX_INT; label = "Point-à-point intérieur"; }
    price = table[sizeCm] || 0;
    breakdown.push({ label: `${label} ${sizeCm}×${sizeCm} cm`, value: price });
  } else if (family === "neon" || family === "croix-neon" || family === "croix-fleche") {
    price = neonPrice(sizeCm);
    breakdown.push({ label: `Croix néon LED ${sizeCm}×${sizeCm} cm`, value: price });
  }

  return { priceCHF: price, breakdown };
}

/**
 * Calcule le prix total de TOUS les items d'une croix.
 * @returns {{ totalCHF: number, lines: Array<{itemId:string, priceCHF:number, label:string}> }}
 */
export function computeCrossTotal({ items, family, designsById }) {
  if (!Array.isArray(items)) return { totalCHF: 0, lines: [] };
  let total = 0;
  const lines = [];
  for (const it of items) {
    const design = designsById[it.designId];
    if (!design) continue;
    const { priceCHF, breakdown } = computeItemPrice({ item: it, family, design });
    if (priceCHF <= 0) continue;
    total += priceCHF;
    lines.push({ itemId: it.id, priceCHF, label: breakdown[0]?.label || "" });
  }
  return { totalCHF: total, lines };
}

export function formatCHF(value) {
  if (!value || Number.isNaN(value)) return "—";
  return `${Math.round(value).toLocaleString("fr-CH")} CHF`;
}

// ============================================================
// LETTRES (boîtier / rétro / inox / châssis / néon intérieur)
// ============================================================
// Prix unitaire (CHF HT) par lettre selon la hauteur de la lettre et le slug
// produit. La grille STANDARD (boîtier alu / rétro / inox / châssis) utilise :
//  - 15 cm → 80 CHF
//  - 80 cm → 700 CHF
// La grille NÉON INTÉRIEUR (slug enseigne-interieur-neon-led) est moins chère :
//  - 15 cm → 33 CHF
//  - 35 cm → 100 CHF
// Interpolation / extrapolation linéaires.
function letterUnitPrice(heightCm, slug = null) {
  if (!heightCm || heightCm <= 0) return 0;
  if (slug === "enseigne-interieur-neon-led") {
    const minH = 15, minP = 33;
    const refH = 35, refP = 100;
    const slope = (refP - minP) / (refH - minH); // 3.35 CHF / cm
    if (heightCm < minH) return minP;
    return Math.round(minP + (heightCm - minH) * slope);
  }
  // Grille standard lettres (boîtier / rétro / inox / châssis).
  const slope = (700 - 80) / (80 - 15); // ≈ 9.538 CHF / cm
  if (heightCm < 15) return 80;
  return Math.round(80 + (heightCm - 15) * slope);
}

/**
 * Compte les caractères "imprimables" d'un texte (espaces NON facturés).
 * @param {string} text
 * @returns {number}
 */
function countLetters(text) {
  if (!text) return 0;
  return [...String(text)].filter((c) => /[^\s]/.test(c)).length;
}

/**
 * Calcule le prix d'une ligne (= un support ou une ligne de texte).
 * @param {object} line - { text, sizeId|heightCm, ... }
 * @param {number} heightCm - hauteur réelle des lettres en cm
 * @param {string} [slug] - slug du produit (pour la grille tarifaire spécifique)
 */
export function computeLetterLinePrice({ line, heightCm, slug = null }) {
  const text = (line?.text || "").trim();
  const letters = countLetters(text);
  if (letters <= 0) return { priceCHF: 0, label: "", letters: 0, unitPrice: 0 };
  const unitPrice = letterUnitPrice(heightCm, slug);
  const priceCHF = unitPrice * letters;
  return {
    priceCHF,
    label: `"${text}" — ${letters} lettre${letters > 1 ? "s" : ""} de ${Math.round(heightCm)} cm`,
    letters,
    unitPrice,
  };
}

/**
 * Calcule le prix total d'un ensemble de lignes lettres.
 * @param {string} [slug] - slug du produit
 * @returns {{ totalCHF: number, lines: Array<{ lineId, priceCHF, label, letters, unitPrice }> }}
 */
export function computeLetterTotal({ lines, getHeightCm, slug = null }) {
  if (!Array.isArray(lines)) return { totalCHF: 0, lines: [] };
  let total = 0;
  const out = [];
  for (const line of lines) {
    const heightCm = typeof getHeightCm === "function" ? getHeightCm(line) : (line?.heightCm || 0);
    const { priceCHF, label, letters, unitPrice } = computeLetterLinePrice({ line, heightCm, slug });
    if (priceCHF <= 0) continue;
    total += priceCHF;
    out.push({ lineId: line.id, priceCHF, label, letters, unitPrice });
  }
  return { totalCHF: total, lines: out };
}

/**
 * Calcule la hauteur en cm d'une ligne (boîtier/rétro/inox/châssis/néon).
 * Centralisé ici pour pouvoir calculer les prix depuis n'importe où, y
 * compris depuis le panneau global multi-pages.
 */
export function getLineHeightCm(line) {
  const sizeId = line?.sizeId || "h40";
  if (sizeId === "h15") return 15;
  if (sizeId === "h25") return 25;
  if (sizeId === "h40") return 40;
  if (sizeId === "h60") return 60;
  if (sizeId === "custom") return Math.max(5, line?.customHeightCm || 40);
  return 40;
}

// Slugs reconnus par la grille tarifaire.
export const PRICED_CROSS_SLUGS = {
  "croix-classique-led": "classic",
  "croix-point-a-point": "dotmatrix",
  "croix-neon-led": "neon",
};
export const PRICED_LETTER_SLUGS = [
  "lettre-boitier-alu",
  "lettre-retro-eclaire",
  "lettre-inox-3d",
  "enseigne-boitier-chassis",
  "enseigne-interieur-neon-led",
];

// Caducée pharmacie — interpolation linéaire 50 cm = 1 000 CHF, 80 cm = 2 000 CHF.
// Plage 50–80 cm uniquement (hors plage : extrapolation linéaire bornée).
export function computeCaduceePrice(sizeCm) {
  const s = Math.max(50, Math.min(80, Number(sizeCm) || 50));
  const slope = (2000 - 1000) / (80 - 50); // 33.33 CHF/cm
  return Math.round(1000 + (s - 50) * slope);
}

// Châssis aluminium (slug "enseigne-boitier-chassis") :
//  - 300 CHF de base par châssis (par ligne / par support)
//  - +100 CHF par mètre supplémentaire au-delà de 1 m
// La largeur du châssis est estimée à partir du texte :
//    widthCm ≈ nombre_de_lettres × heightCm × 0.55 (ratio largeur/hauteur moyen
//    pour des lettres capitales sans serif).
function chassisFramePrice(widthCm) {
  const widthM = (widthCm || 0) / 100;
  const extra = Math.max(0, widthM - 1) * 100;
  return Math.round(300 + extra);
}

function estimateLineWidthCm(letters, heightCm) {
  return letters * heightCm * 0.55;
}

/**
 * Calcule le total et le détail pour UN slug produit donné, en lisant la
 * configuration stockée dans le CompositionContext.
 * Renvoie : { kind: "cross"|"letter"|"none", totalCHF, lines }
 *
 * Pour les croix : `lines = [{itemId, label, priceCHF}]`
 * Pour les lettres : `lines = [{lineId, label, priceCHF, letters, unitPrice}]`
 *
 * Cas spécial enseigne-boitier-chassis : on ajoute après chaque ligne de
 * texte une ligne distincte "Châssis aluminium" avec son prix dédié (300
 * CHF base + 100 CHF/m au-delà de 1 m de largeur estimée).
 */
export function computeSlugPricing({ slug, cfg, designsById }) {
  if (!cfg) return { kind: "none", totalCHF: 0, lines: [] };
  if (PRICED_CROSS_SLUGS[slug]) {
    const family = PRICED_CROSS_SLUGS[slug];
    const items = Array.isArray(cfg.items) ? cfg.items : [];
    const r = computeCrossTotal({ items, family, designsById: designsById || {} });
    return { kind: "cross", totalCHF: r.totalCHF, lines: r.lines };
  }
  if (PRICED_LETTER_SLUGS.includes(slug)) {
    const linesIn = Array.isArray(cfg.lines) ? cfg.lines : [];
    const r = computeLetterTotal({ lines: linesIn, getHeightCm: getLineHeightCm, slug });
    if (slug !== "enseigne-boitier-chassis") {
      return { kind: "letter", totalCHF: r.totalCHF, lines: r.lines };
    }
    // Châssis : on intègre le prix du châssis dans le prix de chaque ligne
    // de texte (PAS de ligne séparée → pas de bouton "Ajouter au panier"
    // dédié). Le client voit un total consolidé par ligne, comprenant
    // lettres + châssis. La largeur est `customWidthCm` si imposée, sinon
    // estimation `letters × heightCm × 0.55`.
    const merged = [];
    let total = 0;
    for (const l of r.lines) {
      const src = linesIn.find((L) => L.id === l.lineId);
      const heightCm = src ? getLineHeightCm(src) : 40;
      const customW = (src?.customWidthCm && src.customWidthCm > 0) ? Number(src.customWidthCm) : null;
      const widthCm = customW || estimateLineWidthCm(l.letters, heightCm);
      const framePrice = chassisFramePrice(widthCm);
      const combinedPrice = l.priceCHF + framePrice;
      total += combinedPrice;
      merged.push({
        lineId: l.lineId,
        priceCHF: combinedPrice,
        label: `${l.label} + châssis ${customW ? "(sur-mesure)" : "≈"} ${(widthCm / 100).toFixed(2)} m`,
        letters: l.letters,
        unitPrice: l.unitPrice,
      });
    }
    return { kind: "letter", totalCHF: total, lines: merged };
  }
  if (slug === "caducee") {
    const sizeCm = Number(cfg?.sizeCm) || 50;
    const price = computeCaduceePrice(sizeCm);
    const finish = cfg?.finishLabel || cfg?.finishId || "—";
    const led = cfg?.ledLabel || cfg?.ledId || "—";
    const place = cfg?.placement === "exterieur" ? "Extérieur" : "Intérieur";
    return {
      kind: "caducee",
      totalCHF: price,
      lines: [{
        lineId: "caducee",
        priceCHF: price,
        label: `Caducée ${sizeCm} cm — ${finish} · LED ${led} · ${place}`,
      }],
    };
  }
  return { kind: "none", totalCHF: 0, lines: [] };
}
